import has from 'lodash/has';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import mapValues from 'lodash/mapValues';
import forOwn from 'lodash/forOwn';
import reduce from 'lodash/reduce';
import mapKeys from 'lodash/mapKeys';
import includes from 'lodash/includes';

import { FLOW_METRICS_INTERVAL_LATEST_RUN } from '../../constants/metricsConstants';
import {
  CLEAN_RESOURCE_FILES_DETAILS,
  CLEAR_FLOW_RUN_LOGS,
  CLEAR_FLOW_RUN_SUMMARY,
  SET_FLOW_METRICS_INTERVAL,
  GET_DASHBOARD_METRICS_SUCCESS,
  GET_FLOW_RUNS_REQUEST,
  GET_FLOW_RUNS_FAILURE,
  GET_FLOW_RUNS_SUCCESS,
  CLEAR_FLOW_RUNS,
  GET_FLOW_METRICS_SUCCESS,
  GET_FLOW_RUN_LOGS_FAILURE,
  GET_FLOW_RUN_LOGS_SUCCESS,
  GET_FLOW_RUN_SUMMARY_SUCCESS,
  GET_ORG_BILLABLE_EVENTS_TOTAL_SUCCESS,
  GET_ORG_RESOURCE_METRICS_N_DAYS_SUCCESS,
  GET_ORG_SINK_METRICS_N_DAYS_SUCCESS,
  GET_ORG_SOURCE_METRICS_N_DAYS_SUCCESS,
  GET_RECORD_LINEAGE_FAILURE,
  GET_RECORD_LINEAGE_SUCCESS,
  GET_RESOURCE_COUNT_REQUEST,
  GET_RESOURCE_COUNT_SUCCESS,
  GET_RESOURCE_FILE_STATS_SUCCESS,
  GET_RESOURCE_FILES_DETAILS_FAILURE,
  GET_RESOURCE_FILES_DETAILS_REQUEST,
  GET_RESOURCE_FILES_DETAILS_SUCCESS,
  GET_RESOURCE_METRICS_AGGREGATE_SUCCESS,
  GET_RESOURCE_METRICS_BY_DAYS_SUCCESS,
  GET_USER_ACCOUNT_RESOURCE_METRICS_AGG_SUCCESS,
  GET_USER_ACCOUNT_SUMMARY_SUCCESS,
  GET_USER_BILLABLE_EVENTS_TOTAL_SUCCESS,
  GET_USER_RESOURCE_METRICS_N_DAYS_SUCCESS,
  GET_USER_SINK_METRICS_N_DAYS_SUCCESS,
  GET_USER_SOURCE_METRICS_N_DAYS_SUCCESS,
  LOGOUT_COMPLETE,
  /**
   * Flow Run Logs with Facets
   */
  CLEAR_FLOW_RUN_LOGS_WITH_FACETS_V2,
  GET_FLOW_RUN_LOGS_WITH_FACETS_REQUEST_V2,
  GET_FLOW_RUN_LOGS_WITH_FACETS_SUCCESS_V2,
  GET_FLOW_RUN_LOGS_WITH_FACETS_FAILURE_V2,
  GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_REQUEST_V2,
  GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_SUCCESS_V2,
  GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_FAILURE_V2,
} from 'js/constants/actionTypes';

import {
  METRICS_RESOURCE_NAME_SINK,
  METRICS_RESOURCE_NAME_SOURCE,
  METRICS_RESOURCE_TYPE_MAPPING,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import Constants from '../../Constants';

import DateUtils from '../../lib/DateUtils';
import { merge } from 'lodash';
import {
  handleFlowsWithMetrics,
  handleFlowRunLogsWithFacets,
  handleFlowRunLogsWithFacetsAutoRefresh,
} from './metrics.utils';

const initialState = {
  user: {},
  org: {},
  resourceCount: {
    currentMode: 'user',
    user: {
      user_id: null,
      org_id: null,
      data_sources: {
        active: 0,
        paused: 0,
        initialized: 0,
        rate_limited: 0,
        total: 0,
      },
      data_sinks: {
        active: 0,
        paused: 0,
        initialized: 0,
        total: 0,
      },
      data_sets: {
        detected: {
          active: 0,
          paused: 0,
          initialized: 0,
          total: 0,
        },
        derived: {
          active: 0,
          paused: 0,
          initialized: 0,
          total: 0,
        },
      },
    },
    org: {
      user_id: null,
      org_id: null,
      data_sources: {
        active: 0,
        paused: 0,
        initialized: 0,
        rate_limited: 0,
        total: 0,
      },
      data_sinks: {
        active: 0,
        paused: 0,
        initialized: 0,
        total: 0,
      },
      data_sets: {
        detected: {
          active: 0,
          paused: 0,
          initialized: 0,
          total: 0,
        },
        derived: {
          active: 0,
          paused: 0,
          initialized: 0,
          total: 0,
        },
      },
    },
  },
  dashboard: {
    lastUpdated: null,
    start_time: null,
    end_time: null,
  },
  [RESOURCE_TYPE_SOURCE]: {},
  [RESOURCE_TYPE_SINK]: {},
  [RESOURCE_TYPE_DATA_SET]: {},
  flowNodeMetricIds: {}, // flowNodeMetricIds[flowNodeId] = { sources: [...], nexsets: [...], sinks: [...] }
  recordLineage: {
    isError: false,
    error: {},
    lineage: {},
  },
  flowRuns: {
    data: [],
    loading: false,
    error: null,
  },
  flowRunSummary: {}, // flowRunSummary[flowNodeId] = metrics
  flowRunLogs: {}, // flowRunLogs[flowNodeId][runId] = logs
  flowRunLogsPaged: {}, // flowRunLogsPaged[flowNodeId] = { meta: any; pagesData: { [page: number]: Log[] } }
  flowRunLogsWithFacets: {
    logs: {},
    total: 0,
    hasMore: undefined,
    nextPageValues: 'initial_request',
    facets: {},
    isLoading: false,
    error: null,
  },
  minLoadedLogPage: 0,
  maxLoadedLogPage: 0,
  flowMetricsInterval: FLOW_METRICS_INTERVAL_LATEST_RUN,
};

export const metricsReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_RESOURCE_METRICS_AGGREGATE_SUCCESS:
      return updateResourceMetricsAggregate(state, action);

    case GET_USER_ACCOUNT_RESOURCE_METRICS_AGG_SUCCESS:
      return updateAccountResourceMetricsAggregate(state, action);

    case GET_RESOURCE_METRICS_BY_DAYS_SUCCESS:
      return updateResourceMetrics7Days(state, action);

    case GET_RESOURCE_FILE_STATS_SUCCESS:
      return updateResourceFileStats(state, action);

    case GET_USER_ACCOUNT_SUMMARY_SUCCESS: {
      let userMetrics = updateUserAccountSummary(state, action);
      return {
        ...state,
        user: userMetrics,
      };
    }

    case GET_RESOURCE_COUNT_REQUEST:
      const { requestInfo } = action;
      const { by } = requestInfo;
      return {
        ...state,
        resourceCount: {
          ...state.resourceCount,
          currentMode: by,
        },
      };

    case GET_RESOURCE_COUNT_SUCCESS: {
      const { requestInfo, result } = action;
      const { by } = requestInfo;
      return {
        ...state,
        resourceCount: {
          ...state.resourceCount,
          currentMode: by,
          lastUpdated: {
            ...state.resourceCount?.lastUpdated,
            [by]: new Date().getTime(),
          },
          [by]: result,
        },
      };
    }

    case GET_USER_BILLABLE_EVENTS_TOTAL_SUCCESS: {
      let userMetrics = updateUserBillableEventsTotal(state, action);
      return {
        ...state,
        user: userMetrics,
      };
    }

    case GET_ORG_BILLABLE_EVENTS_TOTAL_SUCCESS: {
      let orgMetrics = updateOrgBillableEventsTotal(state, action);
      return {
        ...state,
        org: orgMetrics,
      };
    }

    case GET_USER_RESOURCE_METRICS_N_DAYS_SUCCESS:
    case GET_USER_SOURCE_METRICS_N_DAYS_SUCCESS:
    case GET_USER_SINK_METRICS_N_DAYS_SUCCESS: {
      let userMetrics = updateUserResourceMetrics7Days(state, action);
      return {
        ...state,
        user: userMetrics,
      };
    }

    case GET_ORG_RESOURCE_METRICS_N_DAYS_SUCCESS:
    case GET_ORG_SOURCE_METRICS_N_DAYS_SUCCESS:
    case GET_ORG_SINK_METRICS_N_DAYS_SUCCESS: {
      let orgMetrics = updateOrgResourceMetrics7Days(state, action);
      return {
        ...state,
        org: orgMetrics,
      };
    }

    case GET_DASHBOARD_METRICS_SUCCESS:
      return updateDashboardMetrics(state, action);

    case GET_RECORD_LINEAGE_SUCCESS:
    case GET_RECORD_LINEAGE_FAILURE:
      let recordLineageMetrics = updateRecordLineageMetrics(state, action);
      return {
        ...state,
        recordLineage: recordLineageMetrics,
      };

    case GET_FLOW_RUNS_REQUEST:
      return saveFlowRunsRequest(state, action);

    case GET_FLOW_RUNS_SUCCESS:
      return saveFlowRunsSuccess(state, action);

    case GET_FLOW_RUNS_FAILURE:
      return saveFlowRunsFailure(state, action);

    case CLEAR_FLOW_RUNS:
      return clearFlowRuns(state, action);

    case GET_FLOW_METRICS_SUCCESS:
      return saveNodeMetric(state, action);

    case GET_FLOW_RUN_SUMMARY_SUCCESS:
      return saveFlowRunSummary(state, action);

    case CLEAR_FLOW_RUN_SUMMARY:
      return clearFlowRunSummary(state, action);

    case SET_FLOW_METRICS_INTERVAL:
      return {
        ...state,
        flowMetricsInterval: action.interval,
      };

    case GET_FLOW_RUN_LOGS_SUCCESS:
      return saveFlowRunLogs(state, action);

    case GET_FLOW_RUN_LOGS_FAILURE:
      return saveFlowRunErrorLogs(state, action);

    case CLEAR_FLOW_RUN_LOGS:
      return clearFlowRunLogs(state, action);

    case GET_RESOURCE_FILES_DETAILS_REQUEST:
      return saveFileDetailsRequest(state, action);

    case GET_RESOURCE_FILES_DETAILS_SUCCESS:
      return saveFileDetailsSuccess(state, action);

    case GET_RESOURCE_FILES_DETAILS_FAILURE:
      return saveFileDetailsFailure(state, action);

    case CLEAN_RESOURCE_FILES_DETAILS:
      return clearFileDetails(state);

    case LOGOUT_COMPLETE:
      return initialState;

    /**
     * Flow Run Logs with Facets
     */
    case GET_FLOW_RUN_LOGS_WITH_FACETS_REQUEST_V2:
      return {
        ...state,
        flowRunLogsWithFacets: {
          ...state.flowRunLogsWithFacets,
          isLoading: true,
          error: null,
        },
      };

    case GET_FLOW_RUN_LOGS_WITH_FACETS_SUCCESS_V2:
      return {
        ...state,
        flowRunLogsWithFacets: handleFlowRunLogsWithFacets(action, state.flowRunLogsWithFacets),
      };

    case GET_FLOW_RUN_LOGS_WITH_FACETS_FAILURE_V2:
      return {
        ...state,
        flowRunLogsWithFacets: {
          ...state.flowRunLogsWithFacets,
          isLoading: false,
          error: action.error,
        },
      };

    case GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_REQUEST_V2:
      return {
        ...state,
        flowRunLogsWithFacets: {
          ...state.flowRunLogsWithFacets,
          isLoading: true,
          error: null,
        },
      };

    case GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_SUCCESS_V2:
      return {
        ...state,
        flowRunLogsWithFacets: handleFlowRunLogsWithFacetsAutoRefresh(
          action,
          state.flowRunLogsWithFacets,
        ),
      };

    case GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_FAILURE_V2:
      return {
        ...state,
        flowRunLogsWithFacets: {
          ...state.flowRunLogsWithFacets,
          isLoading: false,
          error: action.error,
        },
      };

    case CLEAR_FLOW_RUN_LOGS_WITH_FACETS_V2: {
      return {
        ...state,
        flowRunLogsWithFacets: {
          logs: {},
          total: 0,
          hasMore: undefined,
          nextPageValues: 'initial_request',
          facets: {},
          isLoading: false,
          error: null,
        },
      };
    }
    default:
      return state;
  }
};

export default metricsReducer;

function initUserMetrics() {
  return {
    summary: {},
    accountMetrics: {},
    accountMetricsDaily: {},
    [METRICS_RESOURCE_NAME_SOURCE]: {
      agg: {},
      byDate: {},
    },
    [METRICS_RESOURCE_NAME_SINK]: {
      agg: {},
      byDate: {},
    },
  };
}

function initOrgMetrics() {
  return {
    summary: {},
    accountMetrics: {},
    accountMetricsDaily: {},
    [METRICS_RESOURCE_NAME_SOURCE]: {
      agg: {},
      byDate: {},
    },
    [METRICS_RESOURCE_NAME_SINK]: {
      agg: {},
      byDate: {},
    },
  };
}

function updateUserAccountSummary(state, action) {
  const stateSlice = state.user;
  let {
    requestInfo: { userId },
    result,
    entities: { users },
    receivedAt,
  } = action;

  let thisUserMetrics = has(stateSlice, userId) ? stateSlice[userId] : initUserMetrics();

  let updatedUserMetrics = {
    ...thisUserMetrics,
    summary: {
      lastUpdated: receivedAt,
      metrics: users[result].account_summary,
    },
  };

  return {
    ...stateSlice,
    [userId]: updatedUserMetrics,
  };
}

function updateUserBillableEventsTotal(state, action) {
  const stateSlice = state.user;
  let {
    requestInfo: { userId },
    result,
    receivedAt,
  } = action;

  let thisUserMetrics = has(stateSlice, userId) ? stateSlice[userId] : initUserMetrics();

  let metrics = {};
  if (has(result, 'status') && result.status === 200 && has(result, 'metrics')) {
    metrics = result.metrics;
    if (has(metrics, 'start_time')) {
      metrics.start_time_formatted = DateUtils.convertDateStringToFormat(
        metrics.start_time,
        'MMM D, YYYY (h:mma z)',
      );
    }

    if (has(metrics, 'end_time')) {
      metrics.end_time_formatted = DateUtils.convertDateStringToFormat(
        metrics.end_time,
        'MMM D, YYYY (h:mma z)',
      );
    }
  }

  let updatedUserMetrics = {
    ...thisUserMetrics,
    accountMetrics: {
      lastUpdated: receivedAt,
      metrics,
    },
  };

  return {
    ...stateSlice,
    [userId]: updatedUserMetrics,
  };
}

function updateOrgBillableEventsTotal(state, action) {
  const stateSlice = state.org;
  let {
    requestInfo: { orgId },
    result,
    receivedAt,
  } = action;

  let thisOrgMetrics = has(stateSlice, orgId) ? stateSlice[orgId] : initOrgMetrics();

  let metrics =
    has(result, 'status') && result.status === 200 && has(result, 'metrics') ? result.metrics : {};

  let updatedOrgMetrics = {
    ...thisOrgMetrics,
    accountMetrics: {
      lastUpdated: receivedAt,
      metrics,
    },
  };

  return {
    ...stateSlice,
    [orgId]: updatedOrgMetrics,
  };
}

function updateUserResourceMetrics7Days(state, action) {
  const stateSlice = state.user;
  let {
    requestInfo: { resourceType, userId },
    result: { metrics },
    receivedAt,
  } = action;

  let thisUserMetrics = has(stateSlice, userId) ? stateSlice[userId] : initUserMetrics();

  let receivedMetrics = metrics && !isEmpty(metrics) ? keyBy(metrics, 'time') : {};
  let resourceMetrics = {
    ...thisUserMetrics[resourceType],
    byDate: {
      lastUpdated: receivedAt,
      metrics: receivedMetrics,
    },
  };

  let updatedUserMetrics = {
    ...thisUserMetrics,
    [resourceType]: resourceMetrics,
  };

  return {
    [userId]: updatedUserMetrics,
  };
}

function updateOrgResourceMetrics7Days(state, action) {
  const stateSlice = state.org;
  let {
    requestInfo: { resourceType, orgId },
    result: { metrics },
    receivedAt,
  } = action;

  let thisUserMetrics = has(stateSlice, orgId) ? stateSlice[orgId] : initUserMetrics();

  let receivedMetrics = metrics && !isEmpty(metrics) ? keyBy(metrics, 'time') : {};
  let resourceMetrics = {
    ...thisUserMetrics[resourceType],
    byDate: {
      lastUpdated: receivedAt,
      metrics: receivedMetrics,
    },
  };

  let updatedUserMetrics = {
    ...thisUserMetrics,
    [resourceType]: resourceMetrics,
  };

  return {
    [orgId]: updatedUserMetrics,
  };
}

function updateResourceMetricsAggregate(state, action) {
  let {
    result: { metrics },
    receivedAt,
    requestInfo: { resourceType, id },
  } = action;

  let stateSlice = state[resourceType];
  let thisResouceMetrics = has(stateSlice, id) ? stateSlice[id] : { agg: {}, byDate: {} };

  let updatedResourceMetrics = {
    ...thisResouceMetrics,
    agg: {
      metrics,
      lastUpdated: receivedAt,
    },
  };

  let updatedSlice = {
    ...stateSlice,
    [id]: updatedResourceMetrics,
  };

  return { ...state, [resourceType]: updatedSlice };
}

function updateAccountResourceMetricsAggregate(state, action) {
  let {
    result: { metrics },
    receivedAt,
  } = action;

  let stateUpdates = {};
  forOwn(metrics, (resourceMetrics, resourceType) => {
    let stateResourceType = METRICS_RESOURCE_TYPE_MAPPING[resourceType];
    let stateSlice = state[stateResourceType];
    //new state for this resource type
    let updatedStateSlice = reduce(
      resourceMetrics,
      (currentSlice, thisResource, resourceId) => {
        //get current status of this resource
        let currentResouceMetrics = has(stateSlice, resourceId)
          ? stateSlice[resourceId]
          : { agg: {}, byDate: {} };

        //update aggregates for this resource
        let updatedResourceMetrics = {
          ...currentResouceMetrics,
          agg: {
            metrics: thisResource,
            lastUpdated: receivedAt,
          },
        };
        //update metrics slice
        currentSlice[resourceId] = updatedResourceMetrics;
        return currentSlice;
      },
      {},
    );
    stateUpdates[stateResourceType] = updatedStateSlice;
  });

  return { ...state, ...stateUpdates };
}

function updateResourceMetrics7Days(state, action) {
  let {
    result: { metrics },
    receivedAt,
    requestInfo: { resourceType, id },
  } = action;

  let stateSlice = state[resourceType];

  let thisResouceMetrics = has(stateSlice, id) ? stateSlice[id] : { agg: {}, byDate: {} };
  let receivedMetrics = metrics && !isEmpty(metrics) ? keyBy(metrics, 'time') : {};

  let updatedResourceMetrics = {
    ...thisResouceMetrics,
    byDate: {
      lastUpdated: receivedAt,
      metrics: receivedMetrics,
    },
  };
  let updatedSlice = {};
  forOwn(stateSlice, (currRes, resId) => {
    if (resId !== id) {
      updatedSlice[resId] = { ...currRes, byDate: {} };
    }
  });

  updatedSlice[id] = updatedResourceMetrics;
  return { ...state, [resourceType]: updatedSlice };
}

function updateResourceFileStats(state, action) {
  const {
    result: { metrics },
    receivedAt,
    requestInfo: { resourceType, id },
  } = action;
  const metricsData = metrics.data;
  const fileCountsByStatus = mapValues(
    Constants.IngestionStatus,
    (status) => metricsData[status] || 0,
  );
  const numFiles = Object.values(fileCountsByStatus).reduce(
    (sum, count) => sum + (Number(count) || 0),
    0,
  );
  const notStartedFiles =
    fileCountsByStatus[Constants.IngestionStatus.NOT_STARTED] +
      fileCountsByStatus[Constants.IngestionStatus.QUEUED_REINGEST] || 0;
  const partialSuccessFiles = fileCountsByStatus[Constants.IngestionStatus.PARTIAL] || 0;
  const numCompletedFiles =
    fileCountsByStatus[Constants.IngestionStatus.COMPLETE] +
    fileCountsByStatus[Constants.IngestionStatus.RECOVERED];
  const numErrorFiles = fileCountsByStatus[Constants.IngestionStatus.ERROR] || 0;
  const fileStats = {
    numFiles,
    notStartedFiles,
    partialSuccessFiles,
    numCompletedFiles,
    numErrorFiles,
    lastUpdated: receivedAt,
  };
  const stateSlice = state[resourceType];
  const thisResouceMetrics = has(stateSlice, id) ? stateSlice[id] : {};
  const updatedResourceMetrics = { ...thisResouceMetrics, fileStats };
  const updatedSlice = { ...stateSlice, [id]: updatedResourceMetrics };

  return { ...state, [resourceType]: updatedSlice };
}

function updateDashboardMetrics(state, action) {
  let {
    result: { metrics },
    receivedAt,
  } = action;
  const resourceTypeByResponseKey = {
    sources: RESOURCE_TYPE_SOURCE,
    sinks: RESOURCE_TYPE_SINK,
    datasets: RESOURCE_TYPE_DATA_SET,
    start_time: 'start_time',
    end_time: 'end_time',
  };
  const dashboardMetrics = mapKeys(metrics, (value, key) => resourceTypeByResponseKey[key]);

  let start_time = null,
    end_time = null;

  if (has(metrics, 'start_time')) {
    start_time = includes(metrics.start_time, 'Z') ? metrics.start_time : `${metrics.start_time}Z`;
  }

  if (has(metrics, 'end_time')) {
    end_time = includes(metrics.end_time, 'Z') ? metrics.end_time : `${metrics.end_time}Z`;
  }

  return {
    ...state,
    dashboard: {
      ...dashboardMetrics,
      lastUpdated: receivedAt,
      start_time,
      end_time,
    },
  };
}

function updateRecordLineageMetrics(state, action) {
  let { type, receivedAt } = action;
  let recordLineageMetrics = {
    receivedAt: receivedAt,
    isError: false,
    error: null,
    lineage: {},
  };

  if (type === GET_RECORD_LINEAGE_FAILURE) {
    recordLineageMetrics.isError = true;
    recordLineageMetrics.error = {
      message: has(action, 'message') ? action.message : 'Could not fetch lineage',
      error: has(action, 'error') ? action.error : 'Could not fetch lineage',
    };
  } else if (type === GET_RECORD_LINEAGE_SUCCESS) {
    if (has(action, 'result.status') && action.result.status === 200) {
      recordLineageMetrics.lineage = has(action, 'result.output.id.lineage')
        ? action.result.output.id.lineage
        : {};
    } else {
      recordLineageMetrics.isError = true;
      recordLineageMetrics.error = {
        message: has(action, 'message') ? action.message : 'Could not fetch lineage',
        error: has(action, 'error') ? action.error : 'Could not fetch lineage',
      };
    }
  } else {
    recordLineageMetrics = { ...state.recordLineage };
  }

  return recordLineageMetrics;
}

function saveNodeMetric(state, action) {
  const {
    result: {
      metrics: { data },
    },
    requestInfo: { flowNodeId },
  } = action;

  let data_sets = has(data, RESOURCE_TYPE_DATA_SET)
    ? mapKeys(data[RESOURCE_TYPE_DATA_SET], (val, key) => data[RESOURCE_TYPE_DATA_SET][key].id)
    : {};
  let data_sources = has(data, RESOURCE_TYPE_SOURCE)
    ? mapKeys(data[RESOURCE_TYPE_SOURCE], (val, key) => data[RESOURCE_TYPE_SOURCE][key].id)
    : {};
  let data_sinks = has(data, RESOURCE_TYPE_SINK)
    ? mapKeys(data[RESOURCE_TYPE_SINK], (val, key) => data[RESOURCE_TYPE_SINK][key].id)
    : {};

  // Remove stale IDs from previous load for this flow before merging new data.
  // This ensures nodes absent from the new response are shown as blank rather
  // than retaining data from a prior interval (e.g. Lifetime → Latest Run).
  const prevIds = state.flowNodeMetricIds[flowNodeId] ?? {
    sources: [],
    nexsets: [],
    sinks: [],
  };
  const clearedSources = Object.fromEntries(
    Object.entries(state[RESOURCE_TYPE_SOURCE]).filter(
      ([id]) => !prevIds.sources.includes(Number(id)),
    ),
  );
  const clearedNexsets = Object.fromEntries(
    Object.entries(state[RESOURCE_TYPE_DATA_SET]).filter(
      ([id]) => !prevIds.nexsets.includes(Number(id)),
    ),
  );
  const clearedSinks = Object.fromEntries(
    Object.entries(state[RESOURCE_TYPE_SINK]).filter(([id]) => !prevIds.sinks.includes(Number(id))),
  );

  return {
    ...state,
    [RESOURCE_TYPE_SOURCE]: merge(clearedSources, data_sources),
    [RESOURCE_TYPE_DATA_SET]: merge(clearedNexsets, data_sets),
    [RESOURCE_TYPE_SINK]: merge(clearedSinks, data_sinks),
    flowNodeMetricIds: {
      ...state.flowNodeMetricIds,
      [flowNodeId]: {
        sources: Object.keys(data_sources).map(Number),
        nexsets: Object.keys(data_sets).map(Number),
        sinks: Object.keys(data_sinks).map(Number),
      },
    },
  };
}

/**
 * Flow Runs
 */
function saveFlowRunsRequest(state, action) {
  return {
    ...state,
    runs: {
      data: [],
      loading: true,
      error: null,
    },
  };
}

function saveFlowRunsSuccess(state, action) {
  const { rspHeaders } = action;
  const fetchedFlowsIds = action.result?.flows?.id ?? [];
  const metrics = action.result?.metrics ?? {};
  const metricsValues = metrics && typeof metrics === 'object' ? Object.values(metrics) : [];

  const metricsMappedByOriginNodeId = metricsValues.reduce((acc, metric) => {
    if (metric?.origin_node_id) {
      acc[metric.origin_node_id] = metric;
    }
    return acc;
  }, {});

  return {
    ...state,
    runs: {
      error: false,
      loading: false,
      flowIds: fetchedFlowsIds,
      metricsMappedByOriginNodeId,
      lastUpdated: Date.now(),
      meta: {
        currentPage: rspHeaders?.['X-Current-Page'],
        totalCount: rspHeaders?.['X-Total-Count'],
        pageCount: rspHeaders?.['X-Page-Count'],
        totalPageCount: rspHeaders?.['X-Total-Page-Count'],
      },
    },
  };
}

function saveFlowRunsFailure(state, action) {
  return {
    ...state,
    runs: {
      data: [],
      lastUpdated: Date.now(),
      loading: false,
      error: action?.error || true,
    },
  };
}

function clearFlowRuns(state, action) {
  return {
    ...state,
    runs: {
      data: [],
      loading: false,
      error: null,
    },
  };
}

function saveFlowRunSummary(state, action) {
  const {
    result: { metrics },
    requestInfo: { flowNodeId, page },
  } = action;
  const { flowRunSummary } = state;
  const currentSummary = flowRunSummary[flowNodeId];
  const newSummary =
    !currentSummary || page === 1
      ? metrics
      : { ...metrics, data: { ...currentSummary.data, ...metrics.data } };

  return {
    ...state,
    flowRunSummary: {
      ...flowRunSummary,
      [flowNodeId]: newSummary,
    },
  };
}

/**
 * Flow Run Logs
 */
function clearFlowRunSummary(state, action) {
  const { originNodeId } = action.payload || {};
  if (originNodeId != null) {
    const { [originNodeId]: _removed, ...rest } = state.flowRunSummary;
    return { ...state, flowRunSummary: rest };
  }
  return { ...state, flowRunSummary: initialState.flowRunSummary };
}

function saveFlowRunLogs(state, action) {
  const {
    result: { logs },
    requestInfo: { flowNodeId, runId, page },
  } = action;
  const { flowRunLogs, minLoadedLogPage, maxLoadedLogPage } = state;
  const currentLogs = flowRunLogs[flowNodeId]?.[runId];
  let newLogs;
  let minPage = minLoadedLogPage;
  let maxPage = maxLoadedLogPage;

  // if the loaded page is next to the max page, append the new logs
  if (currentLogs && maxLoadedLogPage && page === maxLoadedLogPage + 1) {
    newLogs = { ...logs, data: [...currentLogs.data, ...logs.data] };
    maxPage = page;
  }

  // if the loaded page is previous to the min page, prepend the new logs
  else if (currentLogs && minLoadedLogPage && page === minLoadedLogPage - 1) {
    newLogs = { ...logs, data: [...logs.data, ...currentLogs.data] };
    minPage = page;
  }

  // if the loaded page is not adjacent to the min or the max pages, overwrite
  // the old logs
  else {
    newLogs = logs;

    if (page) {
      minPage = page;
      maxPage = page;
    }
  }

  const currentFlowRunLogsPaged = state.flowRunLogsPaged?.[flowNodeId];
  const runIdKey = runId ? runId : 'historical';
  const currentFlowRunLogs = currentFlowRunLogsPaged?.[runIdKey];

  return {
    ...state,
    minLoadedLogPage: minPage,
    maxLoadedLogPage: maxPage,
    flowRunLogs: {
      ...flowRunLogs,
      [flowNodeId]: {
        ...flowRunLogs[flowNodeId],
        [runId]: newLogs,
      },
    },
    flowRunLogsPaged: {
      [flowNodeId]: {
        ...currentFlowRunLogsPaged,
        [runIdKey]: {
          meta: {
            ...logs.meta,
            lastTimeUpdated: Date.now(),
          },
          pagesData: {
            ...(currentFlowRunLogs?.pagesData ?? {}),
            [page]: logs.data || [],
          },
        },
      },
    },
  };
}

function saveFlowRunErrorLogs(state, action) {
  const {
    requestInfo: { runId, page },
  } = action;
  const { flowRunLogs } = state;
  const currentLogs = flowRunLogs[flowNodeId]?.[runId];
  const mockError = {
    log: 'There was an error trying to fetch logs',
    timestamp: new Date().getTime(),
    severity: 'ERROR',
  };
  const newLogs =
    !currentLogs || page === 1
      ? { data: [mockError] }
      : { data: [...(currentLogs.data || []), mockError] };

  return {
    ...state,
    flowRunLogs: {
      ...flowRunLogs,
      [flowNodeId]: {
        ...flowRunLogs[flowNodeId],
        [runId]: newLogs,
      },
    },
  };
}

function clearFlowRunLogs(state, action) {
  return {
    ...state,
    flowRunLogs: initialState.flowRunLogs,
    minLoadedLogPage: initialState.minLoadedLogPage,
    maxLoadedLogPage: initialState.maxLoadedLogPage,
    flowRunLogsPaged: initialState.flowRunLogsPaged,
    flowRunLogsWithFacets: initialState.flowRunLogsWithFacets,
  };
}

/**
 * File Details
 */
function saveFileDetailsSuccess(state, action) {
  if (!action.requestInfo?.fileName) return state;

  const data = action?.result?.output?.data ?? [];

  return {
    ...state,
    fileDetails: {
      ...state?.fileDetails,
      [action?.requestInfo?.fileName]: {
        data,
        error: false,
        loading: false,
      },
    },
  };
}

function saveFileDetailsRequest(state, action) {
  if (!action.requestInfo?.fileName) return state;

  return {
    ...state,
    fileDetails: {
      ...state?.fileDetails,
      [action.requestInfo.fileName]: {
        ...state?.fileDetails?.[action.requestInfo.fileName],
        loading: true,
      },
    },
  };
}

function saveFileDetailsFailure(state, action) {
  if (!action.requestInfo?.fileName) return state;

  return {
    ...state,
    fileDetails: {
      ...state?.fileDetails,
      [action.requestInfo.fileName]: {
        ...state?.fileDetails?.[action.requestInfo.fileName],
        loading: false,
        error: action?.error || true,
      },
    },
  };
}

function clearFileDetails(state) {
  return {
    ...state,
    fileDetails: {},
  };
}
